<template>
  <RecursiveWrapper
    :components="
      wrapperDecorations.map((comp) => ({
        ...comp,
        props: comp.propsFn(row),
      }))
    "
    first-component-class="grid-view__row-background-wrapper"
  >
    <div
      class="grid-view__row"
      :data-row-id="row.id"
      :class="{
        'grid-view__row--selected': isRowHighlighted(row),
        'grid-view__row--loading': row._.loading,
        'grid-view__row--hover': row._.hover,
        'grid-view__row--warning': !row._.matchFilters || !row._.matchSearch,
        'grid-view__row--has-status': !!statusField,
        'grid-view__row--subtasks-open': subtasksExpanded,
        'grid-view__row--adding-subtask': addingSubtask,
        'grid-view__row--cell-selected': row._.selected,
      }"
      @mouseover="$emit('row-hover', { row, value: true })"
      @mouseleave="$emit('row-hover', { row, value: false })"
      @contextmenu.prevent="$emit('row-context', { row, event: $event })"
      @pointerdown="rowHoldDown($event, row)"
    >
      <!-- A "linha" propriamente dita: altura fixa e colunas em flex. O
           bloco de subtarefas fica FORA dela, no fluxo, para empurrar as
           linhas de baixo em vez de cobri-las. -->
      <div class="grid-view__row-line">
        <template v-if="includeRowDetails">
          <!-- Origem Flow: linha fora da ordenação (status trocado, grupo
             novo) NÃO ganha aviso nem borda — mover já comunica. Os avisos
             de filtro/busca ficam: nesses casos a linha vai SUMIR no
             refresh, e isso o usuário precisa saber. -->
          <div
            v-if="!row._.matchFilters || !row._.matchSearch"
            class="grid-view__row-warning"
          >
            <template v-if="!row._.matchFilters">
              {{ $t('gridViewRow.rowNotMatchingFilters') }}
            </template>
            <template v-else-if="!row._.matchSearch">
              {{ $t('gridViewRow.rowNotMatchingSearch') }}
            </template>
          </div>
          <div
            class="grid-view__column grid-view__column--no-border-right"
            :class="{ 'grid-view__column--group-end': groupEnd }"
            :style="{ width: gridViewRowDetailsWidth + 'px' }"
          >
            <div
              class="grid-view__row-info"
              :class="{
                'grid-view__row-info--matches-search':
                  row._.matchSearch &&
                  row._.fieldSearchMatches.includes('row_id'),
              }"
            >
              <div
                v-if="isCheckboxSelected(row.id) || row._.hover"
                class="grid-view__row-checkbox"
              >
                <Checkbox
                  :checked="isCheckboxSelected(row.id)"
                  :disabled="isCheckboxDisabled(row.id)"
                  :size="'small'"
                  @input="toggleRowCheckbox"
                ></Checkbox>
              </div>
              <div
                v-else
                class="grid-view__row-count"
                :class="{
                  'grid-view__row-count--small': rowIdentifier > 9999,
                  'grid-view__row-count--hide-on-hover': canDrag,
                }"
                :title="rowIdentifier"
              >
                <div class="grid-view__row-count-content">
                  {{ rowIdentifier }}
                </div>
              </div>
              <div
                v-if="!readOnly && canDrag"
                class="grid-view__row-drag"
                @pointerdown="startDragging($event, row)"
              ></div>
              <component
                :is="rowExpandButton"
                v-if="!row._.loading"
                :row="row"
                :workspace-id="workspaceId"
                :table="view.table"
                @edit-modal="onEditModal"
              ></component>
              <a
                v-if="!row._.loading && canCopyRowLink"
                class="grid-view__row-copy-link"
                :title="$t('gridView.copyRowLink')"
                @click.stop="copyRowLink(row)"
              >
                <i class="iconoir-link"></i>
              </a>
              <!-- Origem Flow: seta de expandir subtarefas à esquerda do anel
                 de status, como no ClickUp — sempre visível quando há
                 filhas, no hover nas demais linhas. -->
              <a
                v-if="
                  !row._.loading &&
                  (subtaskCount > 0 ||
                    subtasksExpanded ||
                    (row._.hover && !readOnly))
                "
                class="grid-view__row-caret"
                :title="$t('gridView.openSubtasks')"
                @click.stop="toggleSubtasksExpanded"
              >
                <i
                  class="iconoir-nav-arrow-right"
                  :class="{ 'grid-view__row-caret--open': subtasksExpanded }"
                ></i>
              </a>
              <!-- Origem Flow: ícone de status estilo ClickUp ao lado do nome
                 da tarefa — anel na cor do status (check verde quando
                 concluída); o clique abre o menu de troca de status. -->
              <a
                v-if="statusField && !row._.loading"
                class="grid-view__row-status"
                :class="statusIconClasses"
                :title="$t('gridView.changeStatus')"
                @click.stop="openStatusMenu"
              >
                <i v-if="statusIsDone" class="iconoir-check"></i>
              </a>
              <Context
                v-if="statusMenuRendered"
                ref="statusContext"
                overflow-scroll
                max-height-if-outside-viewport
              >
                <div class="grid-status-menu">
                  <a
                    v-for="option in statusField.select_options"
                    :key="option.id"
                    class="grid-status-menu__item"
                    @click="setStatus(option)"
                  >
                    <span
                      class="grid-status-menu__chip"
                      :class="'background-color--' + option.color"
                      >{{ option.value }}</span
                    >
                    <i
                      v-if="statusOption && statusOption.id === option.id"
                      class="iconoir-check grid-status-menu__check"
                    ></i>
                  </a>
                </div>
              </Context>
              <component
                :is="dec.component"
                v-for="dec in firstCellDecorations"
                :key="dec.decoration.id"
                v-bind="dec.propsFn(row)"
              />
            </div>
          </div>
          <!-- Origem Flow: subtarefas estilo ClickUp no fim da célula do
             título — contador "⑂ n" (abre o modal da linha, onde as
             subtarefas ficam listadas) e "+" no hover para criar uma
             subtarefa ligada a esta linha. -->
          <div
            v-if="
              primaryFieldRendered &&
              (subtaskCount > 0 ||
                (row._.hover && !readOnly) ||
                subtasksExpanded ||
                addingSubtask)
            "
            class="grid-view__row-subtasks"
            :style="{ left: subtasksOverlayLeft + 'px' }"
          >
            <a
              v-if="subtaskCount > 0"
              class="grid-view__row-subtasks-count"
              :title="$t('gridView.openSubtasks')"
              @click.stop="toggleSubtasksExpanded"
            >
              <i class="iconoir-git-fork"></i>{{ subtaskCount }}
            </a>
            <a
              v-if="row._.hover && !readOnly && !row._.loading"
              class="grid-view__row-subtasks-add"
              :title="$t('gridView.addSubtask')"
              @click.stop="openSubtasksForAdd"
            >
              <i class="iconoir-plus"></i>
            </a>
          </div>
          <!-- Origem Flow: coluna Comentários do ClickUp — balão em TODAS
             as linhas, na posição da última coluna virtual (fim lógico da
             linha, compensado pelo deslocamento do buffer). O clique abre
             o popover ancorado com a conversa e o campo para comentar. -->
          <div
            v-if="!row._.loading"
            class="grid-view__row-commentcol"
            :style="{ left: commentColLeft + 'px' }"
          >
            <a
              class="grid-view__row-comments"
              :class="{ 'grid-view__row-comments--empty': commentCount === 0 }"
              :title="$t('gridView.openComments')"
              @click.stop="toggleCommentsPanel"
            >
              <i class="iconoir-message"></i
              ><template v-if="commentCount > 0">{{ commentCount }}</template>
            </a>
            <Context
              v-if="commentsRendered"
              ref="commentsContext"
              overflow-scroll
              max-height-if-outside-viewport
              @hidden="commentsPanelOpen = false"
            >
              <div class="grid-comments-popover">
                <RowCommentsSidebar
                  :database="database"
                  :table="view.table"
                  :row="row"
                  :view="view"
                ></RowCommentsSidebar>
              </div>
            </Context>
          </div>
        </template>
        <!--
      Somehow re-declaring all the events instead of using v-on="$listeners" speeds
      everything up because the rows don't need to be updated everytime a new one is
      rendered, which happens a lot when scrolling.
      -->
        <GridViewCell
          v-for="field in fieldsToRender"
          :key="'row-field-' + row._.persistentId + '-' + field.id.toString()"
          :workspace-id="workspaceId"
          :field="field"
          :row="row"
          :all-fields-in-table="allFieldsInTable"
          :state="state"
          :multi-select-position="getMultiSelectPosition(row.id, field)"
          :read-only="readOnly || !canWriteFieldValues(field)"
          :store-prefix="storePrefix"
          :group-end="groupEnd"
          :style="{
            width: fieldWidths[field.id] + 'px',
            ...getSelectedCellStyle(field),
          }"
          :is-selected="isCellSelected(field.id)"
          :is-alive="isAlive(field.id)"
          :add-keep-alive="addKeepAlive"
          :remove-keep-alive="removeKeepAlive"
          :class="{ 'grid-view__column--primary-link': field.primary }"
          @click="primaryCellClick(field)"
          @update="$emit('update', $event)"
          @paste="$emit('paste', $event)"
          @edit="$emit('edit', $event)"
          @select="$emit('select', $event)"
          @unselect="$emit('unselect', $event)"
          @selected="$emit('selected', $event)"
          @unselected="$emit('unselected', $event)"
          @select-next="$emit('select-next', $event)"
          @refresh-row="$emit('refresh-row', $event)"
          @cell-mousedown-left="$emit('cell-mousedown-left', { row, field })"
          @cell-mouseover="$emit('cell-mouseover', { row, field })"
          @cell-mouseup-left="$emit('cell-mouseup-left', { row, field })"
          @cell-shift-click="$emit('cell-shift-click', { row, field })"
          @add-row-after="$emit('add-row-after', $event)"
          @edit-modal="$emit('edit-modal', row)"
          @select-cell="selectCell"
          @set-state="setState"
        ></GridViewCell>
      </div>
      <template v-if="includeRowDetails">
        <!-- Expansão aninhada estilo ClickUp: as subtarefas aparecem como
             linhas do próprio grid logo abaixo da mãe (indentadas, altura
             de linha idêntica), com a linha de criação inline no fim —
             Enter salva em série, Esc/Cancelar fecha. -->
        <div
          v-if="subtasksExpanded"
          ref="subtasksBlock"
          class="grid-view__subtasks"
          @mousedown.stop
        >
          <div
            v-if="subtasksLoading"
            class="grid-view__subtasks-row grid-view__subtasks-row--loading"
            :style="{ paddingLeft: subtasksIndent + 'px' }"
          >
            {{ $t('gridView.loadingSubtasks') }}
          </div>
          <template v-else>
            <a
              v-for="child in subtaskRows"
              :key="child.id"
              class="grid-view__subtasks-row"
              :style="{ paddingLeft: subtasksIndent + 'px' }"
              @click.stop="openSubtask(child)"
            >
              <span
                class="grid-view__row-status grid-view__subtasks-status"
                :class="childStatusClasses(child)"
              >
                <i v-if="childIsDone(child)" class="iconoir-check"></i>
              </span>
              <span
                class="grid-view__subtasks-name"
                :style="{ maxWidth: subtaskNameMaxWidth + 'px' }"
                >{{ childName(child) }}</span
              >
              <!-- Colunas da subtarefa: mesmas colunas da tabela, na
                   mesma posição e com o mesmo visual das tarefas. O clique
                   abre o editor do campo (o mesmo do modal da linha). -->
              <span
                v-for="col in subtaskColumns"
                :key="col.field.id"
                class="grid-view__subtasks-cell"
                :class="{
                  'grid-view__subtasks-cell--editable': canWriteFieldValues(
                    col.field
                  ),
                }"
                :style="{ left: col.left + 'px', width: col.width + 'px' }"
                @click.stop.prevent="openSubtaskField(child, col.field, $event)"
              >
                <component
                  :is="functionalFieldComponent(col.field)"
                  :workspace-id="workspaceId"
                  :row="child"
                  :field="col.field"
                  :value="child['field_' + col.field.id]"
                  :state="state"
                  :read-only="true"
                  :store-prefix="storePrefix"
                />
              </span>
            </a>
            <div
              v-if="addingSubtask"
              class="grid-view__subtasks-row grid-view__subtasks-row--new"
              :style="{ paddingLeft: subtasksIndent + 'px' }"
              @click.stop
            >
              <span
                class="grid-view__row-status grid-view__row-status--empty grid-view__subtasks-status"
              ></span>
              <input
                ref="newSubtaskInput"
                v-model="newSubtaskName"
                class="grid-view__subtasks-input"
                :placeholder="$t('gridView.subtaskNamePlaceholder')"
                :disabled="creatingSubtask"
                @keydown.stop
                @keydown.enter="submitNewSubtask()"
                @keydown.tab.prevent="tabNewSubtask"
                @keydown.esc="cancelNewSubtask"
                @click.stop
              />
              <a
                class="grid-view__subtasks-cancel"
                @click.stop="cancelNewSubtask"
                >{{ $t('action.cancel') }}</a
              >
              <a
                class="grid-view__subtasks-save"
                @click.stop="submitNewSubtask()"
                >{{ $t('gridView.saveSubtask') }}
                <span class="grid-view__subtasks-save-key">↵</span></a
              >
            </div>
            <a
              v-else-if="!readOnly"
              class="grid-view__subtasks-row grid-view__subtasks-row--add"
              :style="{ paddingLeft: subtasksIndent + 'px' }"
              @click.stop="startAddingSubtask"
            >
              <i class="iconoir-plus"></i>
              {{ $t('gridView.addSubtask') }}
            </a>
          </template>
        </div>
        <!-- Editor do responsável da subtarefa: Context teleportado para o
             body, então não é cortado pelo scroll do bloco de subtarefas. -->
        <!-- Sem `overflow-scroll`: ele deixa o Context com overflow hidden e
             corta a lista de colaboradores, que é absoluta dentro dele. -->
        <Context
          v-if="subtaskEditorRendered"
          ref="subtaskEditorContext"
          @hidden="onSubtaskEditorHidden"
        >
          <div
            v-if="subtaskEditChild && subtaskEditField"
            ref="subtaskEditorBox"
            class="grid-view__subtasks-editor"
            @click.stop
            @mousedown.stop
          >
            <div class="grid-view__subtasks-editor-label">
              {{ subtaskEditField.name }}
            </div>
            <component
              :is="subtaskEditComponent"
              :field="subtaskEditField"
              :value="subtaskEditChild['field_' + subtaskEditField.id]"
              :read-only="false"
              :workspace-id="workspaceId"
              :row="subtaskEditChild"
              :all-fields-in-table="allFieldsInTable"
              @update="updateSubtaskField"
            />
          </div>
        </Context>
      </template>
    </div>
  </RecursiveWrapper>
</template>

<script>
import Context from '@baserow/modules/core/components/Context'
import GridViewCell from '@baserow/modules/database/components/view/grid/GridViewCell'
import { copyToClipboard } from '@baserow/modules/database/utils/clipboard'
import {
  getStatusField,
  isDoneOption,
} from '@baserow/modules/database/utils/dueDate'
import {
  getAssigneeField,
  getParentTaskField,
  getSubtasksField,
} from '@baserow/modules/database/utils/subtasks'
import RowService from '@baserow/modules/database/services/row'
import RowCommentsSidebar from '@baserow_premium/components/row_comments/RowCommentsSidebar'
import { notifyIf } from '@baserow/modules/core/utils/error'
import gridViewHelpers from '@baserow/modules/database/mixins/gridViewHelpers'
import GridViewRowExpandButton from '@baserow/modules/database/components/view/grid/GridViewRowExpandButton'
import RecursiveWrapper from '@baserow/modules/core/components/RecursiveWrapper'
import { GRID_VIEW_MULTI_SELECT_AREA } from '@baserow/modules/database/constants'

export default {
  name: 'GridViewRow',
  components: {
    Context,
    GridViewRowExpandButton,
    GridViewCell,
    RecursiveWrapper,
    RowCommentsSidebar,
  },
  mixins: [gridViewHelpers],
  provide() {
    return {
      $hasPermission: this.$hasPermission,
    }
  },
  props: {
    view: {
      type: Object,
      required: true,
    },
    workspaceId: {
      type: Number,
      required: true,
    },
    row: {
      type: Object,
      required: true,
    },
    groupEnd: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    /**
     * Origem Flow: a expansão das subtarefas é controlada pelo GridViewRows
     * — a janela virtual recria os componentes de linha e um estado local
     * fechava o bloco sozinho.
     */
    subtasksExpanded: {
      type: Boolean,
      required: false,
      default: false,
    },
    renderedFields: {
      type: Array,
      required: true,
    },
    decorationsByPlace: {
      type: Object,
      required: true,
    },
    visibleFields: {
      type: Array,
      required: true,
    },
    allVisibleFields: {
      type: Array,
      required: true,
    },
    allFieldsInTable: {
      type: Array,
      required: true,
    },
    fieldWidths: {
      type: Object,
      required: true,
    },
    includeRowDetails: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeGroupBy: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
    canDrag: {
      type: Boolean,
      required: true,
    },
    rowIdentifierType: {
      type: String,
      required: false,
      default: 'count',
    },
    count: {
      type: Number,
      required: true,
    },
    // Origem Flow: deslocamento horizontal do buffer de colunas (o
    // container das linhas translada até a primeira coluna renderizada);
    // os overlays ancorados por posição lógica compensam com ele.
    leftOffset: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  emits: [
    'update',
    'paste',
    'edit',
    'cell-mousedown-left',
    'cell-mouseover',
    'cell-mouseup-left',
    'cell-shift-click',
    'cell-selected',
    'selected',
    'unselected',
    'select',
    'unselect',
    'select-next',
    'add-row-after',
    'edit-modal',
    'refresh-row',
    'row-dragging',
    'row-hover',
    'row-context',
    'toggle-subtasks',
    'expand-subtasks',
    'collapse-subtasks',
  ],
  data() {
    return {
      // The state can be used by functional components to make changes to the dom.
      // This is for example used by the functional file field component to enable the
      // drop effect without having the cell selected.
      state: {},
      // A list containing field id's of field cells that must not be converted to the
      // functional component even though the user has selected another cell. This is
      // for example used by the file field to finish the uploading task if the user
      // has selected another cell while uploading.
      alive: [],
      rowExpandButton: markRaw(
        this.$registry
          .get('application', 'database')
          .getRowExpandButtonComponent()
      ),
      // O Context do menu de status só é montado no primeiro clique
      // (lazy) para não pesar nas dezenas de linhas renderizadas.
      statusMenuRendered: false,
      // A expansão em si vive no GridViewRows (prop subtasksExpanded): a
      // janela virtual recicla os componentes de linha, e um estado local
      // fazia o bloco fechar sozinho a cada re-render.
      subtasksLoading: false,
      // Popover de comentários da linha (coluna Comentários do ClickUp).
      commentsRendered: false,
      commentsPanelOpen: false,
      subtaskRows: [],
      // Editor de campo da subtarefa (Context lazy, como o de status).
      subtaskEditorRendered: false,
      subtaskEditChildId: null,
      subtaskEditFieldId: null,
      // Input inline de criação (usabilidade do ClickUp).
      addingSubtask: false,
      creatingSubtask: false,
      newSubtaskName: '',
      // Sessão de teclado do editor de subtarefa: enquanto o editor está
      // aberto, Tab/Shift+Tab andam pelas colunas da mesma subtarefa e Esc
      // fecha. Ativada tanto pelo Tab no input de título quanto pelo clique
      // numa coluna.
      subtaskSessionActive: false,
      subtaskSessionNavigating: false,
      subtaskSessionKeydown: null,
    }
  },
  computed: {
    /**
     * Ação rápida "copiar link" (estilo ClickUp): só na página autenticada
     * da tabela, onde a rota do modal da linha existe.
     */
    canCopyRowLink() {
      return this.$route.name === 'database-table'
    },
    /**
     * Origem Flow: campo de status (mesma heurística dos contadores e das
     * datas) usado pelo ícone de status da linha.
     */
    statusField() {
      return getStatusField(this.allFieldsInTable)
    },
    statusOption() {
      if (!this.statusField) {
        return null
      }
      return this.row[`field_${this.statusField.id}`] || null
    },
    statusIsDone() {
      return isDoneOption(this.statusOption)
    },
    subtasksField() {
      return getSubtasksField(this.allFieldsInTable, this.view.table_id)
    },
    parentTaskField() {
      return getParentTaskField(this.allFieldsInTable, this.view.table_id)
    },
    /**
     * Origem Flow: campo de responsável — cada subtarefa tem o seu, editável
     * e independente do responsável da tarefa mãe (que só serve de padrão
     * na criação).
     */
    assigneeField() {
      return getAssigneeField(this.allFieldsInTable)
    },
    /**
     * Colunas da subtarefa: as mesmas colunas visíveis da tabela (menos a
     * primária, que é o nome), com a posição e a largura da coluna da mãe.
     * É o que dá à subtarefa o mesmo comportamento das tarefas na lista.
     */
    subtaskColumns() {
      let left = this.gridViewRowDetailsWidth - this.leftOffset
      const columns = []
      this.visibleFields.forEach((field) => {
        const width = this.fieldWidths[field.id] || 0
        if (!field.primary) {
          columns.push({ field, left, width })
        }
        left += width
      })
      return columns
    },
    /**
     * O nome não pode invadir a primeira coluna: para no fim da coluna do
     * título, descontando a indentação e o anel de status.
     */
    subtaskNameMaxWidth() {
      const firstColumn = this.subtaskColumns[0]
      const end = firstColumn
        ? firstColumn.left
        : this.gridViewRowDetailsWidth - this.leftOffset
      return Math.max(80, end - this.subtasksIndent - 40)
    },
    subtaskEditChild() {
      if (this.subtaskEditChildId === null) {
        return null
      }
      return (
        this.subtaskRows.find(
          (child) => child.id === this.subtaskEditChildId
        ) || null
      )
    },
    subtaskEditField() {
      if (this.subtaskEditFieldId === null) {
        return null
      }
      return (
        this.allFieldsInTable.find(
          (field) => field.id === this.subtaskEditFieldId
        ) || null
      )
    },
    subtaskEditComponent() {
      if (!this.subtaskEditField) {
        return null
      }
      return this.$registry
        .get('field', this.subtaskEditField.type)
        .getRowEditFieldComponent(this.subtaskEditField)
    },
    /**
     * Origem Flow: contagem de comentários da linha (metadata anotada
     * pelo premium) e o objeto database para o popover de comentários.
     */
    commentCount() {
      return (this.row._ && this.row._.metadata?.row_comment_count) || 0
    },
    database() {
      return this.$store.getters['application/get'](this.view.table.database_id)
    },
    /**
     * O overlay fica no FIM da célula do título (primeira coluna após os
     * detalhes da linha), qualquer que seja a seção que a contenha —
     * com freeze o título vive na seção esquerda; sem freeze, na direita.
     */
    subtasksOverlayLeft() {
      const primaryField = this.allFieldsInTable.find((field) => field.primary)
      const primaryWidth = primaryField
        ? this.fieldWidths[primaryField.id] || 0
        : 0
      return this.gridViewRowDetailsWidth + primaryWidth - this.leftOffset
    },
    /**
     * Origem Flow: o cluster do título só existe enquanto a célula do
     * título está renderizada — com scroll horizontal o buffer descarta
     * as colunas fora da janela e a âncora perderia o sentido.
     */
    primaryFieldRendered() {
      return this.renderedFields.some((field) => field.primary)
    },
    /**
     * Posição lógica da coluna virtual de Comentários: logo após a última
     * coluna visível, compensando o deslocamento do buffer.
     */
    commentColLeft() {
      const total = this.visibleFields.reduce(
        (sum, field) => sum + (this.fieldWidths[field.id] || 0),
        0
      )
      return this.gridViewRowDetailsWidth + total - this.leftOffset + 8
    },
    /**
     * Indentação das pseudo-linhas de subtarefa: o anel da filha começa no
     * início da célula do título, ~22px à direita do anel da mãe — a mesma
     * relação medida no ClickUp.
     */
    subtasksIndent() {
      return this.gridViewRowDetailsWidth
    },
    subtaskCount() {
      if (!this.subtasksField) {
        return 0
      }
      const value = this.row[`field_${this.subtasksField.id}`]
      return Array.isArray(value) ? value.length : 0
    },
    statusIconClasses() {
      if (!this.statusOption) {
        return ['grid-view__row-status--empty']
      }
      return [
        `background-color--${this.statusOption.color}`,
        this.statusIsDone
          ? 'grid-view__row-status--done'
          : 'grid-view__row-status--ring',
      ]
    },
    /**
     * This component already accepts a `renderedFields` property containing the fields
     * that must be rendered based on the viewport width and horizontal scroll offset,
     * meaning it only renders the fields that are in the viewport. Because a selected
     * field must always be rendered, this computed property checks if there is a
     * selected field and if so, it's added to the array. This doesn't influence the
     * position of the other cells because the position will be absolute. The selected
     * field must always be rendered, otherwise the arrow keys and other functionality
     * won't work.
     */
    fieldsToRender() {
      // If the row doesn't have a selected field, we can safely return the fields
      // because we just want to render the fields inside of the view port.
      if (!this.row._.selected) {
        return this.renderedFields
      }

      // Check if the selected field exists in the all fields array, so not just the to
      // be rendered ones.
      const selectedField = this.visibleFields.find(
        (field) => field.id === this.row._.selectedFieldId
      )

      // If it doesn't exist or if it's already in the fields array, we don't have to
      // add it because it's already rendered.
      if (
        selectedField === undefined ||
        this.renderedFields.find((field) => field.id === selectedField.id) !==
          undefined
      ) {
        return this.renderedFields
      }

      // If the selected field exists in all fields, but not in fields it must be added
      // to the fields array because we want to render it. It won't influence the other
      // cells because it's positioned absolute.
      const fields = this.renderedFields.slice()
      fields.unshift(selectedField)
      return fields
    },
    firstCellDecorations() {
      return this.decorationsByPlace?.first_cell || []
    },
    wrapperDecorations() {
      return this.decorationsByPlace?.wrapper || []
    },
    rowIdentifier() {
      switch (this.rowIdentifierType) {
        case 'count':
          return this.count
        default:
          return this.row.id
      }
    },
  },
  watch: {
    subtasksExpanded: {
      immediate: true,
      handler(value) {
        if (value) {
          if (this.subtaskCount > 0) {
            this.fetchSubtaskRows()
          } else {
            this.subtaskRows = []
          }
        } else {
          this.addingSubtask = false
          this.newSubtaskName = ''
          const editorContext = this.$refs.subtaskEditorContext
          if (editorContext) {
            editorContext.hide()
          }
        }
      },
    },
  },
  mounted() {
    // Edições da subtarefa feitas fora do bloco (modal da linha, realtime)
    // não passam pela cópia local `subtaskRows` — chegam por este evento.
    this.$bus.$on('table-row-updated', this.onTableRowUpdated)
  },
  beforeUnmount() {
    this.$bus.$off('table-row-updated', this.onTableRowUpdated)
    // A janela virtual recicla as linhas: a sessão de teclado não pode
    // deixar listener global para trás.
    this.endSubtaskSession()
  },
  methods: {
    copyRowLink(row) {
      const { href } = this.$router.resolve({
        name: 'database-table-row',
        params: {
          databaseId: this.view.table.database_id,
          tableId: this.view.table_id,
          viewId: this.view.id,
          rowId: row.id,
        },
      })
      copyToClipboard(`${window.location.origin}${href}`)
      this.$store.dispatch('toast/info', {
        title: this.$t('gridView.copyRowLink'),
        message: this.$t('gridView.rowLinkCopied'),
      })
    },
    onEditModal() {
      this.$emit('edit-modal', this.row)
    },
    openStatusMenu(event) {
      if (
        this.readOnly ||
        !this.statusField ||
        !this.canWriteFieldValues(this.statusField)
      ) {
        return
      }
      const target = event.currentTarget
      this.statusMenuRendered = true
      this.$nextTick(() => {
        this.$refs.statusContext.toggle(target, 'bottom', 'left', 4)
      })
    },
    /**
     * Origem Flow: expansão aninhada — mostra as subtarefas como linhas
     * inline sob a mãe e busca os dados completos (o valor do link só tem
     * id+nome; o status de cada filha vem do fetch). Com zero filhas, a
     * seta já abre direto no modo de criação, como o "+" do ClickUp.
     */
    toggleSubtasksExpanded() {
      this.$emit('toggle-subtasks', this.row)
      // Abrindo sem nenhuma filha, já entra no modo de criação (o "+" do
      // ClickUp). O estado vive no pai, então só dá para checar depois.
      this.$nextTick(() => {
        if (
          this.subtasksExpanded &&
          this.subtaskCount === 0 &&
          !this.readOnly
        ) {
          this.startAddingSubtask()
        }
      })
    },
    onTableRowUpdated({ tableId, rowId, values }) {
      if (tableId !== this.view.table_id) {
        return
      }
      const index = this.subtaskRows.findIndex((child) => child.id === rowId)
      if (index === -1) {
        return
      }
      this.subtaskRows.splice(index, 1, {
        ...this.subtaskRows[index],
        ...values,
      })
    },
    async fetchSubtaskRows() {
      const links = (this.row[`field_${this.subtasksField.id}`] || []).slice(
        0,
        25
      )
      this.subtasksLoading = true
      try {
        const responses = await Promise.all(
          links.map((link) =>
            RowService(this.$client).get(this.view.table_id, link.id, false)
          )
        )
        this.subtaskRows = responses.map((response) => response.data)
      } catch (error) {
        notifyIf(error, 'row')
      } finally {
        this.subtasksLoading = false
      }
    },
    childName(child) {
      const primaryField = this.allFieldsInTable.find((field) => field.primary)
      const value = primaryField ? child[`field_${primaryField.id}`] : ''
      return value || this.$t('gridView.unnamedSubtask')
    },
    childStatusOption(child) {
      const statusField = getStatusField(this.allFieldsInTable)
      return statusField ? child[`field_${statusField.id}`] || null : null
    },
    childIsDone(child) {
      return isDoneOption(this.childStatusOption(child))
    },
    childStatusClasses(child) {
      const option = this.childStatusOption(child)
      if (!option) {
        return ['grid-view__row-status--empty']
      }
      return [
        `background-color--${option.color}`,
        this.childIsDone(child)
          ? 'grid-view__row-status--done'
          : 'grid-view__row-status--ring',
      ]
    },
    /**
     * Origem Flow: mesmo comportamento das tarefas — clicar na coluna da
     * subtarefa abre o editor daquele campo (o mesmo componente do modal
     * da linha), num Context teleportado para o body (escapa do scroll e
     * do empilhamento do bloco).
     */
    functionalFieldComponent(field) {
      return this.$registry
        .get('field', field.type)
        .getFunctionalGridViewFieldComponent(field)
    },
    openSubtaskField(child, field, event) {
      if (this.readOnly || !this.canWriteFieldValues(field)) {
        return
      }
      this.clearGridCellSelection()
      const target = event.currentTarget
      const same =
        this.subtaskEditChildId === child.id &&
        this.subtaskEditFieldId === field.id
      this.subtaskEditorRendered = true
      this.subtaskEditChildId = child.id
      this.subtaskEditFieldId = field.id
      this.startSubtaskSession()
      // Na primeira vez o Context ainda está montando (v-if lazy), por isso
      // a espera por ele em vez de um $nextTick só.
      let attempts = 0
      const open = () => {
        const context = this.$refs.subtaskEditorContext
        if (!context) {
          if (attempts++ < 5) {
            this.$nextTick(open)
          }
          return
        }
        if (same) {
          context.toggle(target, 'bottom', 'left', 4)
        } else {
          context.show(target, 'bottom', 'left', 4)
        }
        // Um clique só, como nas tarefas: já abre a lista de opções ou o
        // calendário. Precisa insistir: enquanto o Context ainda está
        // oculto (visibility hidden) o navegador se recusa a focar o input.
        // O $el do Context é um nó de fragmento no Vue 3 (não tem
        // querySelector) — a busca sai do ref do próprio conteúdo.
        const activate = (tries) => {
          const el = this.$refs.subtaskEditorBox
          if (!el || !el.querySelector) {
            if (tries < 6) {
              setTimeout(() => activate(tries + 1), 60)
            }
            return
          }
          const selected = el.querySelector('.dropdown__selected')
          const input = el.querySelector('input:not([type="hidden"]), textarea')
          if (selected) {
            selected.click()
            const opened = el.querySelector('.dropdown__items:not(.hidden)')
            if (opened) {
              return
            }
          } else if (input) {
            input.focus()
            if (document.activeElement === input) {
              return
            }
          } else {
            return
          }
          if (tries < 6) {
            setTimeout(() => activate(tries + 1), 60)
          }
        }
        setTimeout(() => activate(0), 30)
      }
      this.$nextTick(open)
    },
    /**
     * Sessão de teclado do editor de subtarefa: Tab/Shift+Tab andam pelas
     * colunas da mesma subtarefa (setas e Enter dentro do dropdown já são
     * nativos), Esc fecha. Depois da última coluna o foco volta para o
     * input de nova subtarefa, se estiver aberto — dá para emendar a
     * próxima sem tocar no mouse.
     */
    startSubtaskSession() {
      this.subtaskSessionActive = true
      if (this.subtaskSessionKeydown === null) {
        this.subtaskSessionKeydown = (event) => {
          if (event.key === 'Tab') {
            event.preventDefault()
            event.stopPropagation()
            this.advanceSubtaskSession(event.shiftKey ? -1 : 1)
          } else if (event.key === 'Escape') {
            this.endSubtaskSession(true)
          }
        }
        document.addEventListener('keydown', this.subtaskSessionKeydown)
      }
    },
    endSubtaskSession(hideEditor = false) {
      if (this.subtaskSessionKeydown !== null) {
        document.removeEventListener('keydown', this.subtaskSessionKeydown)
        this.subtaskSessionKeydown = null
      }
      this.subtaskSessionActive = false
      if (hideEditor) {
        const context = this.$refs.subtaskEditorContext
        if (context) {
          context.hide()
        }
      }
    },
    /**
     * O Context fecha por clique fora, Esc ou programaticamente. Se não foi
     * a própria sessão navegando entre colunas, a sessão acaba junto.
     */
    onSubtaskEditorHidden() {
      if (!this.subtaskSessionNavigating) {
        this.endSubtaskSession()
      }
    },
    advanceSubtaskSession(delta) {
      // Valor digitado (ex.: data) só grava no blur do input do editor —
      // commita antes de trocar de coluna.
      const box = this.$refs.subtaskEditorBox
      const active = document.activeElement
      if (box && active && box.contains(active)) {
        active.blur()
      }
      const child = this.subtaskEditChild
      const columns = this.subtaskColumns
      if (!child || columns.length === 0) {
        this.endSubtaskSession(true)
        return
      }
      const index = columns.findIndex(
        (col) => col.field.id === this.subtaskEditFieldId
      )
      let next = index + delta
      while (
        next >= 0 &&
        next < columns.length &&
        !this.canWriteFieldValues(columns[next].field)
      ) {
        next += delta
      }
      if (next < 0 || next >= columns.length) {
        // Fim (ou início) da linha: fecha o editor e devolve o foco para o
        // input de criação, pronto para a próxima subtarefa.
        this.endSubtaskSession(true)
        if (this.addingSubtask) {
          this.$nextTick(() => {
            const input = this.$refs.newSubtaskInput
            if (input) {
              input.focus()
            }
          })
        }
        return
      }
      this.subtaskSessionNavigating = true
      this.openSubtaskFieldByIndex(child, next)
      this.$nextTick(() => {
        this.subtaskSessionNavigating = false
      })
    },
    /**
     * Abre o editor da coluna `index` da subtarefa, ancorado na célula real
     * do bloco (o alvo precisa existir no DOM — logo após criar a subtarefa
     * a linha ainda está renderizando, daí a nova tentativa).
     */
    openSubtaskFieldByIndex(child, index, retries = 3) {
      const column = this.subtaskColumns[index]
      if (!column) {
        return
      }
      const target = this.subtaskCellElement(child, index)
      if (!target) {
        if (retries > 0) {
          this.$nextTick(() =>
            this.openSubtaskFieldByIndex(child, index, retries - 1)
          )
        }
        return
      }
      this.openSubtaskField(child, column.field, { currentTarget: target })
    },
    subtaskCellElement(child, columnIndex) {
      const block = this.$refs.subtasksBlock
      if (!block) {
        return null
      }
      const rowIndex = this.subtaskRows.findIndex((row) => row.id === child.id)
      if (rowIndex === -1) {
        return null
      }
      const rowEls = block.querySelectorAll(
        '.grid-view__subtasks-row:not(.grid-view__subtasks-row--new):not(.grid-view__subtasks-row--add):not(.grid-view__subtasks-row--loading)'
      )
      const rowEl = rowEls[rowIndex]
      if (!rowEl) {
        return null
      }
      const cells = rowEl.querySelectorAll('.grid-view__subtasks-cell')
      return cells[columnIndex] || null
    },
    /**
     * Persiste o campo da subtarefa. A filha não vive no buffer do grid (o
     * filtro de raiz a esconde), então a gravação é direta na API: valor
     * otimista na cópia local, conversão pelo tipo do campo e rollback no
     * erro. Depois recarrega a linha para trazer o que o backend calcula
     * (fórmulas, avatares, formatação).
     */
    async updateSubtaskField(value, oldValue) {
      const child = this.subtaskEditChild
      const field = this.subtaskEditField
      if (!child || !field) {
        return
      }
      const key = `field_${field.id}`
      const previous = child[key]
      child[key] = value
      try {
        const fieldType = this.$registry.get('field', field.type)
        await RowService(this.$client).update(this.view.table_id, child.id, {
          [key]: fieldType.prepareValueForUpdate(field, value),
        })
        const { data } = await RowService(this.$client).get(
          this.view.table_id,
          child.id,
          false
        )
        const index = this.subtaskRows.findIndex((row) => row.id === child.id)
        if (index !== -1) {
          this.subtaskRows.splice(index, 1, data)
        }
        // Campos de um valor só (status, prioridade, data) fecham depois da
        // escolha; os de vários (responsável, rótulos) ficam abertos. Na
        // sessão de teclado, escolher um status/prioridade já emenda na
        // próxima coluna — o fluxo "seta, Enter, escolhe a próxima". O
        // update pode chegar DEPOIS de a sessão já ter trocado de coluna
        // (data commitada no blur do Tab): aí não fecha nem avança nada.
        const stillCurrent =
          this.subtaskEditFieldId === field.id &&
          this.subtaskEditChildId === child.id
        const multiple = ['multiple_collaborators', 'multiple_select']
        if (stillCurrent && !multiple.includes(field.type)) {
          const context = this.$refs.subtaskEditorContext
          if (this.subtaskSessionActive && field.type === 'single_select') {
            this.subtaskSessionNavigating = true
            if (context) {
              context.hide()
            }
            this.$nextTick(() => this.advanceSubtaskSession(1))
          } else if (context) {
            context.hide()
          }
        }
      } catch (error) {
        child[key] = previous
        notifyIf(error, 'row')
      }
    },
    openSubtask(child) {
      this.$router.push({
        name: 'database-table-row',
        params: {
          databaseId: this.view.table.database_id,
          tableId: this.view.table_id,
          viewId: this.view.id,
          rowId: child.id,
        },
      })
    },
    /**
     * Usabilidade do ClickUp: o "+" expande (mesmo sem filhas) já com a
     * linha de criação inline focada no fim da lista.
     */
    openSubtasksForAdd() {
      if (!this.subtasksExpanded) {
        this.$emit('expand-subtasks', this.row)
      }
      this.$nextTick(() => {
        this.startAddingSubtask()
      })
    },
    /**
     * Origem Flow: popover de comentários ancorado na linha — mesma
     * usabilidade da coluna Comentários do ClickUp (ler e comentar sem
     * abrir a tarefa).
     */
    toggleCommentsPanel(event) {
      const target = event.currentTarget
      this.commentsRendered = true
      this.$nextTick(() => {
        this.$refs.commentsContext.toggle(target, 'bottom', 'right', 4)
        this.commentsPanelOpen = !this.commentsPanelOpen
      })
    },
    /**
     * Uma célula do grid selecionada continua com os listeners globais de
     * teclado ativos — digitar no input da subtarefa abriria o dropdown da
     * célula e roubaria o foco. Criação e edição de subtarefa começam
     * desselecionando a célula.
     */
    clearGridCellSelection() {
      this.$store.dispatch(this.storePrefix + 'view/grid/setSelectedCell', {
        rowId: -1,
        fieldId: -1,
        fields: this.allFieldsInTable,
      })
    },
    startAddingSubtask() {
      this.clearGridCellSelection()
      this.addingSubtask = true
      this.$nextTick(() => {
        const input = this.$refs.newSubtaskInput
        if (input) {
          input.focus()
          input.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    cancelNewSubtask() {
      this.newSubtaskName = ''
      this.addingSubtask = false
      if (this.subtaskRows.length === 0) {
        this.$emit('collapse-subtasks', this.row)
      }
    },
    /**
     * Garante o par de campos do modelo (mãe "Subtarefas" / filha "Tarefa
     * principal" — dois self-links independentes, já que o produto proíbe
     * related field em self-link) e o filtro "Tarefa principal está vazio"
     * que esconde as subtarefas do nível raiz da view. Campos novos nascem
     * ocultos como coluna.
     */
    async ensureSubtaskFields() {
      for (const [existing, nameKey] of [
        [this.subtasksField, 'gridView.subtasksFieldName'],
        [this.parentTaskField, 'gridView.parentTaskFieldName'],
      ]) {
        if (!existing) {
          await this.$store.dispatch('field/create', {
            type: 'link_row',
            table: this.view.table,
            values: {
              name: this.$t(nameKey),
              link_row_table_id: this.view.table_id,
            },
          })
          const created =
            nameKey === 'gridView.subtasksFieldName'
              ? this.subtasksField
              : this.parentTaskField
          if (created) {
            await this.$store.dispatch(
              this.storePrefix + 'view/grid/updateFieldOptionsOfField',
              {
                field: created,
                values: { hidden: true },
                oldValues: { hidden: false },
              }
            )
          }
        }
      }
    },
    async ensureRootFilter(parentField) {
      const filters = this.view.filters || []
      const exists = filters.some(
        (filter) => filter.field === parentField.id && filter.type === 'empty'
      )
      if (exists) {
        return
      }
      // Adicionar uma condição só é seguro em conjunção E. Numa view com
      // OU na raiz, preservamos a lógica atual movendo os filtros para um
      // grupo OU e passando a raiz para E — aí o filtro de raiz entra ao
      // lado do grupo. Se a view já tem grupos, deixamos como está (o
      // usuário decide manualmente).
      if (filters.length > 0 && this.view.filter_type !== 'AND') {
        if ((this.view.filter_groups || []).length > 0) {
          return
        }
        const { filterGroup } = await this.$store.dispatch(
          'view/createFilterGroup',
          { view: this.view }
        )
        await this.$store.dispatch('view/updateFilterGroup', {
          filterGroup,
          values: { filter_type: 'OR' },
        })
        for (const filter of [...filters]) {
          const field = this.allFieldsInTable.find(
            (candidate) => candidate.id === filter.field
          )
          if (!field) {
            continue
          }
          await this.$store.dispatch('view/createFilter', {
            view: this.view,
            field,
            values: {
              field: filter.field,
              type: filter.type,
              value: filter.value,
            },
            filterGroupId: filterGroup.id,
            emitEvent: false,
          })
          await this.$store.dispatch('view/deleteFilter', {
            view: this.view,
            filter,
          })
        }
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { filter_type: 'AND' },
        })
      }
      await this.$store.dispatch('view/createFilter', {
        view: this.view,
        field: parentField,
        values: { field: parentField.id, type: 'empty', value: '' },
        emitEvent: false,
      })
    },
    /**
     * Cria a subtarefa com o nome digitado: a filha nasce já com "Tarefa
     * principal" apontando para a mãe (via API direta — com o filtro de
     * raiz ativo o evento de criação nem insere a linha no grid), o
     * vínculo "Subtarefas" entra na mãe pelo fluxo de update das células.
     * Depois limpa o input e mantém o foco para a próxima, como no ClickUp.
     */
    /**
     * Tab no input de título: cria a subtarefa e já abre o editor da
     * primeira coluna (Responsável) — criação fluida só no teclado. Com o
     * título vazio o Tab não faz nada.
     */
    tabNewSubtask() {
      if (this.newSubtaskName.trim() !== '' && !this.creatingSubtask) {
        this.submitNewSubtask(true)
      }
    },
    async submitNewSubtask(focusFields = false) {
      const name = this.newSubtaskName.trim()
      if (name === '' || this.creatingSubtask) {
        return
      }
      this.creatingSubtask = true
      try {
        await this.ensureSubtaskFields()
        const subtasksField = this.subtasksField
        const parentField = this.parentTaskField
        if (!subtasksField || !parentField) {
          return
        }
        await this.ensureRootFilter(parentField)
        const primaryField = this.allFieldsInTable.find(
          (field) => field.primary
        )
        const values = {
          [`field_${primaryField.id}`]: name,
          [`field_${parentField.id}`]: [this.row.id],
        }
        const statusField = getStatusField(this.allFieldsInTable)
        const inheritedStatus =
          statusField && this.row[`field_${statusField.id}`]
        if (inheritedStatus) {
          values[`field_${statusField.id}`] = inheritedStatus.id
        }
        // O responsável da mãe entra como PADRÃO na subtarefa — depois é
        // editável e independente (pode trocar ou somar responsáveis).
        const assigneeField = this.assigneeField
        const inheritedAssignees =
          assigneeField && this.row[`field_${assigneeField.id}`]
        if (Array.isArray(inheritedAssignees) && inheritedAssignees.length) {
          values[`field_${assigneeField.id}`] = inheritedAssignees.map(
            (collaborator) => collaborator.id
          )
        }
        const { data: newRow } = await RowService(this.$client).create(
          this.view.table_id,
          values
        )
        // Dispatch direto (não $emit): eventos emitidos durante re-renders
        // do grid podem morrer no caminho.
        const current = this.row[`field_${subtasksField.id}`] || []
        await this.$store.dispatch(
          this.storePrefix + 'view/grid/updateRowValue',
          {
            table: this.view.table,
            view: this.view,
            fields: this.allFieldsInTable,
            row: this.row,
            field: subtasksField,
            value: [...current, { id: newRow.id, value: name }],
            oldValue: current,
          }
        )
        this.subtaskRows.push(newRow)
        this.newSubtaskName = ''
        if (focusFields) {
          // Tab: em vez de voltar para o input, abre o editor da primeira
          // coluna editável da subtarefa recém-criada.
          this.$nextTick(() => {
            const columns = this.subtaskColumns
            let first = 0
            while (
              first < columns.length &&
              !this.canWriteFieldValues(columns[first].field)
            ) {
              first += 1
            }
            if (first < columns.length) {
              this.openSubtaskFieldByIndex(newRow, first)
            }
          })
        } else {
          this.$nextTick(() => {
            const input = this.$refs.newSubtaskInput
            if (input) {
              input.focus()
            }
          })
        }
      } catch (error) {
        notifyIf(error, 'row')
      } finally {
        this.creatingSubtask = false
      }
    },
    setStatus(option) {
      const oldValue = this.statusOption
      if (!oldValue || oldValue.id !== option.id) {
        this.$emit('update', {
          row: this.row,
          field: this.statusField,
          value: option,
          oldValue,
        })
      }
      this.$refs.statusContext.hide()
    },
    /**
     * Origem Flow: como no ClickUp, clicar no campo primário (título) abre o
     * modal da linha. O clique só dispara quando o mouseup acontece na mesma
     * célula do mousedown, então arrastar para multi-seleção não abre o
     * modal. A edição inline do título continua possível via teclado
     * (selecionar a célula e apertar Enter).
     */
    primaryCellClick(field) {
      if (field.primary && !this.row._.loading) {
        this.$emit('edit-modal', this.row)
      }
    },
    isCheckboxDisabled(rowId) {
      const checkboxSelectedRows =
        this.$store.state[this.storePrefix + 'view/grid'].checkboxSelectedRows
      return (
        checkboxSelectedRows.length >=
          this.$config.public.baserowRowPageSizeLimit &&
        !checkboxSelectedRows.includes(rowId)
      )
    },
    isCheckboxSelected(rowId) {
      return this.$store.state[
        this.storePrefix + 'view/grid'
      ].checkboxSelectedRows.includes(rowId)
    },
    isRowHighlighted(row) {
      // const selectionType =
      //   this.$store.getters[this.storePrefix + 'view/grid/getSelectionType']
      return (
        // selectionType === GRID_VIEW_MULTI_SELECT_AREA &&
        row._.selectedBy.length > 0
      )
    },
    isCellSelected(fieldId) {
      return this.row._.selected && this.row._.selectedFieldId === fieldId
    },
    isAlive(fieldId) {
      return this.alive.includes(fieldId)
    },
    selectCell(fieldId, rowId = this.row.id) {
      this.$emit('cell-selected', { fieldId, rowId })
    },
    // Return an object that represents if a cell is selected,
    // and it's current position in the selection grid
    getMultiSelectPosition(rowId, field) {
      const position = {
        selected: false,
        top: false,
        right: false,
        bottom: false,
        left: false,
      }
      const selectionType =
        this.$store.getters[this.storePrefix + 'view/grid/getSelectionType']
      if (selectionType !== GRID_VIEW_MULTI_SELECT_AREA) {
        if (this.isCheckboxSelected(rowId)) {
          position.selected = true
        }
        return position
      }

      if (
        this.$store.getters[this.storePrefix + 'view/grid/isMultiSelectActive']
      ) {
        const rowIndex =
          this.$store.getters[this.storePrefix + 'view/grid/getRowIndexById'](
            rowId
          )

        const fieldIndex = this.allVisibleFields.findIndex(
          (f) => f.id === field.id
        )

        const [minRow, maxRow] =
          this.$store.getters[
            this.storePrefix + 'view/grid/getMultiSelectRowIndexSorted'
          ]
        const [minField, maxField] =
          this.$store.getters[
            this.storePrefix + 'view/grid/getMultiSelectFieldIndexSorted'
          ]

        if (rowIndex >= minRow && rowIndex <= maxRow) {
          if (fieldIndex >= minField && fieldIndex <= maxField) {
            position.selected = true
            if (rowIndex === minRow) {
              position.top = true
            }
            if (rowIndex === maxRow) {
              position.bottom = true
            }
            if (fieldIndex === minField) {
              position.left = true
            }
            if (fieldIndex === maxField) {
              position.right = true
            }
          }
        }
      }
      return position
    },
    setState(value) {
      this.state = value
    },
    addKeepAlive(fieldId) {
      if (!this.alive.includes(fieldId)) {
        this.alive.push(fieldId)
      }
    },
    removeKeepAlive(fieldId) {
      const index = this.alive.findIndex((id) => id === fieldId)
      if (index > -1) {
        this.alive.splice(index, 1)
      }
    },
    // Origem Flow: segurar ~220ms parado em QUALQUER ponto da linha inicia o
    // arrasto (mover logo continua selecionando células, como planilha).
    rowHoldDown(event, row) {
      if (this.readOnly || !this.canDrag) return
      if (event.pointerType !== 'touch' && event.button !== 0) return
      if (
        event.target.closest(
          '.grid-view__row-drag, a, button, input, textarea, [contenteditable]'
        )
      )
        return
      const startX = event.clientX
      const startY = event.clientY
      const cancel = () => {
        clearTimeout(timer)
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', cancel)
      }
      const onMove = (e) => {
        if (
          Math.abs(e.clientX - startX) > 5 ||
          Math.abs(e.clientY - startY) > 5
        )
          cancel()
      }
      const timer = setTimeout(() => {
        cancel()
        this.$emit('row-dragging', { row, event })
      }, 220)
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', cancel)
    },
    startDragging(event, row) {
      if (this.readOnly) {
        return
      }

      event.preventDefault()
      this.$emit('row-dragging', { row, event })
    },
    /**
     * Returns an object with additional styling if the field is selected and outside
     * of the viewport. This is because selected fields must always be rendered because
     * otherwise certain functionality won't work.
     */
    getSelectedCellStyle(field) {
      const exists =
        this.renderedFields.find((f) => f.id === field.id) !== undefined

      // If the field already exists in the field list it means that it's already
      // rendered. In that case we don't have to provide any other styling because it's
      // already in the position it's supposed to be in.
      if (exists) {
        return {}
      }

      // If the field doesn't exist in the fields array, it's being rendered because
      // it's selected. In that case, the element must be positioned without influencing
      // the other cells.
      const styling = { position: 'absolute' }

      const selectedFieldIndex = this.visibleFields.findIndex(
        (field) => field.id === this.row._.selectedFieldId
      )
      const firstVisibleFieldIndex = this.visibleFields.findIndex(
        (field) => field.id === this.renderedFields[0].id
      )
      const lastVisibleFieldIndex = this.visibleFields.findIndex(
        (field) =>
          field.id === this.renderedFields[this.renderedFields.length - 1].id
      )

      // Positions the selected field cell on the right position without influencing the
      // position of the rendered cells. This is needed because other components depend
      // on the cell to be in the right position, for example when using the arrow key
      // navigation.
      if (selectedFieldIndex < firstVisibleFieldIndex) {
        // If the selected field must be positioned before the other fields
        let spaceBetween = 0
        for (let i = selectedFieldIndex; i < firstVisibleFieldIndex; i++) {
          spaceBetween += this.fieldWidths[this.visibleFields[i].id]
        }
        styling.left = -spaceBetween + 'px'
      } else if (selectedFieldIndex > lastVisibleFieldIndex) {
        // If the selected field must be positioned after the other fields.
        let spaceBetween = 0
        for (let i = lastVisibleFieldIndex; i < selectedFieldIndex; i++) {
          spaceBetween += this.fieldWidths[this.visibleFields[i].id]
        }
        styling.right = -spaceBetween + 'px'
      }

      return styling
    },
    canWriteFieldValues(field) {
      return this.$registry.get('field', field.type).canWriteFieldValues(field)
    },
    toggleRowCheckbox() {
      this.$store.dispatch(
        this.storePrefix + 'view/grid/toggleCheckboxRowSelection',
        { row: this.row }
      )
    },
  },
}
</script>
